/**
 * Storemapper Accessibility Fix
 * Fixes WCAG compliance issues with the Storemapper widget
 * Addresses:
 * 1. "Elements with role=listbox must contain or own an element with role=option"
 * 2. "The aria-checked attribute must not be used on input[type=radio] or input[type=checkbox]"
 */

(function () {
  "use strict";

  // Configuration
  const WIDGET_SELECTOR = "#storemapper";
  const LISTBOX_SELECTOR = '.strmpr-autocomplete[role="listbox"]';
  const AUTOCOMPLETE_ITEM_SELECTOR =
    ".strmpr-autocomplete-item, .strmpr-suggestion, .strmpr-result";
  const PROBLEMATIC_INPUT_SELECTOR =
    'input[type="radio"][aria-checked], input[type="checkbox"][aria-checked]';

  // Maximum wait time for widget to load (30 seconds)
  const MAX_WAIT_TIME = 30000;
  const CHECK_INTERVAL = 500;

  /**
   * Fix ARIA roles for listbox elements
   */
  function fixListboxAccessibility() {
    const listboxes = document.querySelectorAll(LISTBOX_SELECTOR);

    listboxes.forEach((listbox) => {
      // Get all potential option elements within the listbox
      const optionElements = listbox.querySelectorAll(
        AUTOCOMPLETE_ITEM_SELECTOR
      );

      if (optionElements.length === 0) {
        // If no option elements found, create a hidden one to satisfy WCAG
        const hiddenOption = document.createElement("div");
        hiddenOption.setAttribute("role", "option");
        hiddenOption.setAttribute("aria-hidden", "true");
        hiddenOption.style.display = "none";
        hiddenOption.textContent = "No results";
        listbox.appendChild(hiddenOption);
      } else {
        // Add role="option" to existing elements
        optionElements.forEach((element, index) => {
          if (!element.getAttribute("role")) {
            element.setAttribute("role", "option");
          }

          // Ensure proper aria-selected and id attributes
          if (!element.getAttribute("id")) {
            element.setAttribute("id", `storemapper-option-${index}`);
          }

          if (!element.getAttribute("aria-selected")) {
            element.setAttribute("aria-selected", "false");
          }
        });
      }

      // Ensure listbox has proper aria-expanded and aria-activedescendant
      if (!listbox.getAttribute("aria-expanded")) {
        listbox.setAttribute("aria-expanded", "false");
      }
    });
  }

  /**
   * Fix aria-checked on radio and checkbox inputs
   * The aria-checked attribute must not be used on input[type=radio] or input[type=checkbox]
   */
  function fixInputAriaChecked() {
    const problematicInputs = document.querySelectorAll(
      'input[type="radio"][aria-checked], input[type="checkbox"][aria-checked]'
    );

    problematicInputs.forEach((input) => {
      const ariaCheckedValue = input.getAttribute("aria-checked");

      // Convert aria-checked value to boolean for checked property
      if (ariaCheckedValue === "true") {
        input.checked = true;
      } else if (ariaCheckedValue === "false") {
        input.checked = false;
      }

      // Remove the invalid aria-checked attribute
      input.removeAttribute("aria-checked");
    });
  }

  /**
   * Apply all accessibility fixes
   */
  function applyAllAccessibilityFixes() {
    fixListboxAccessibility();
    fixInputAriaChecked();
  }

  /**
   * Monitor for changes in the autocomplete dropdown
   */
  function setupMutationObserver() {
    const targetNode = document.querySelector(WIDGET_SELECTOR);
    if (!targetNode) return;

    const observer = new MutationObserver(function (mutations) {
      let shouldRecheck = false;

      mutations.forEach(function (mutation) {
        // Check if new nodes were added that might contain accessibility issues
        if (mutation.type === "childList") {
          mutation.addedNodes.forEach(function (node) {
            if (node.nodeType === Node.ELEMENT_NODE) {
              if (
                node.matches &&
                (node.matches(LISTBOX_SELECTOR) ||
                  node.matches(PROBLEMATIC_INPUT_SELECTOR))
              ) {
                shouldRecheck = true;
              } else if (
                node.querySelector &&
                (node.querySelector(LISTBOX_SELECTOR) ||
                  node.querySelector(PROBLEMATIC_INPUT_SELECTOR))
              ) {
                shouldRecheck = true;
              }
            }
          });
        }

        // Check if attributes changed that might affect accessibility
        if (mutation.type === "attributes") {
          const target = mutation.target;
          const attributeName = mutation.attributeName;

          if (target.matches) {
            // Check for class or role changes on listboxes
            if (
              (attributeName === "class" || attributeName === "role") &&
              target.matches(LISTBOX_SELECTOR)
            ) {
              shouldRecheck = true;
            }

            // Check for aria-checked attribute added to radio/checkbox inputs
            if (
              attributeName === "aria-checked" &&
              target.matches('input[type="radio"], input[type="checkbox"]')
            ) {
              shouldRecheck = true;
            }
          }
        }
      });

      if (shouldRecheck) {
        // Small delay to ensure DOM is stable
        setTimeout(applyAllAccessibilityFixes, 100);
      }
    });

    observer.observe(targetNode, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ["class", "role", "aria-checked"],
    });

    return observer;
  }

  /**
   * Wait for Storemapper widget to initialize
   */
  function waitForWidget() {
    let attempts = 0;
    const maxAttempts = MAX_WAIT_TIME / CHECK_INTERVAL;

    function checkWidget() {
      attempts++;

      const widgetContainer = document.querySelector(WIDGET_SELECTOR);
      const hasStoremapperElements = document.querySelector(
        ".strmpr-container, .strmpr-map, .strmpr-autocomplete"
      );

      if (widgetContainer && hasStoremapperElements) {
        // Widget is loaded, set up accessibility fixes
        applyAllAccessibilityFixes();
        setupMutationObserver();

        // Also fix on window load in case elements are added later
        window.addEventListener("load", function () {
          setTimeout(applyAllAccessibilityFixes, 1000);
        });

        return true;
      }

      if (attempts < maxAttempts) {
        setTimeout(checkWidget, CHECK_INTERVAL);
      } else {
        console.warn(
          "Storemapper accessibility fix: Widget not found after " +
            MAX_WAIT_TIME +
            "ms"
        );
      }

      return false;
    }

    checkWidget();
  }

  /**
   * Initialize accessibility fixes when DOM is ready
   */
  function init() {
    if (document.readyState === "loading") {
      document.addEventListener("DOMContentLoaded", waitForWidget);
    } else {
      waitForWidget();
    }
  }

  // Start the accessibility fix
  init();
})();
